import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Copy, Share2, ThumbsDown, ThumbsUp } from "lucide-react";
import { SOFT } from "./soft-tokens";
import { useChatShare } from "./chat-share-button";

export type AnswerVote = "up" | "down";

/** What a vote carries to the API (`voteAnswer`), all of it already on the turn. */
export interface AnswerVoteInput {
  turnId: string;
  vote: AnswerVote;
  question: string;
  answer: string;
  lang: string;
  reason?: string;
}

/**
 * The plain text of an answer, for the clipboard: the `[[slug|Label]]` names
 * the renderer turns into links come out as their label, and markdown marks
 * are dropped so the paste reads as prose.
 */
export function answerPlainText(text: string): string {
  return text
    .replace(/\[\[[^|\]]+\|([^\]]+)\]\]/g, "$1")
    .replace(/\[\[([^\]]+)\]\]/g, "$1")
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .trim();
}

const BTN =
  "mj-press grid h-8 w-8 place-items-center rounded-full transition-colors hover:bg-[rgba(17,14,57,0.055)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1";

/**
 * Under a finished answer (chat kit C29): thumbs up, thumbs down, copy, share.
 *
 *   · Thumbs only when the answer has a server `turnId` to file the vote
 *     against (AssistantTurn files it with `voteAnswer`). A thumbs down asks
 *     "Thanks. What was missing?" with a one-line field; the reason goes as a
 *     second call for the same turn, which the API updates rather than adds.
 *   · Copy puts the answer's text on the clipboard.
 *   · Share is the chat's own share link, so it only shows when the chat is
 *     saved on an account (`shareSessionId`). A signed-out chat lives in one
 *     browser and has nothing to share.
 *
 * No suggested next questions here or anywhere under an answer (owner rule,
 * 2026-10-09: chips only when the assistant asks or refuses off-topic).
 */
export function AnswerActions({
  text,
  turnId,
  question,
  lang,
  onVote,
  shareSessionId,
}: {
  text: string;
  turnId?: string;
  question?: string;
  lang: string;
  onVote?: (input: AnswerVoteInput) => void | Promise<unknown>;
  shareSessionId?: string | null;
}) {
  const { t } = useTranslation();
  const [vote, setVote] = useState<AnswerVote | null>(null);
  const [copied, setCopied] = useState(false);
  const [reason, setReason] = useState("");
  const [reasonSent, setReasonSent] = useState(false);
  const { share, copied: shared } = useChatShare(shareSessionId, lang);
  const canVote = !!turnId && !!onVote;

  const send = (v: AnswerVote, why?: string) => {
    if (!turnId || !onVote) return;
    try {
      void Promise.resolve(
        onVote({
          turnId,
          vote: v,
          question: question ?? "",
          answer: text,
          lang,
          ...(why ? { reason: why } : {}),
        }),
      ).catch(() => {
        // Feedback is best effort: a failed vote must not disturb the chat.
      });
    } catch {
      // Same: never let a vote break the answer it sits under.
    }
  };

  const pick = (v: AnswerVote) => {
    if (vote === v) return;
    setVote(v);
    setReasonSent(false);
    send(v);
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(answerPlainText(text));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // No clipboard (insecure context): nothing to do, the text is on screen.
    }
  };

  return (
    <div className="mj-fade-in mt-2 flex flex-col gap-1" data-testid="answer-actions">
      <div className="flex flex-wrap items-center gap-0.5" style={{ color: SOFT.body }}>
        {canVote ? (
          <>
            <button
              type="button"
              aria-label={t("ai_concierge.turn.helpful", { defaultValue: "Helpful" })}
              aria-pressed={vote === "up"}
              onClick={() => pick("up")}
              className={BTN}
              style={vote === "up" ? { background: "rgba(17,14,57,0.055)", color: SOFT.ink } : undefined}
            >
              <ThumbsUp size={14} aria-hidden="true" fill={vote === "up" ? "currentColor" : "none"} />
            </button>
            <button
              type="button"
              aria-label={t("ai_concierge.turn.not_helpful", { defaultValue: "Not helpful" })}
              aria-pressed={vote === "down"}
              onClick={() => pick("down")}
              className={BTN}
              style={vote === "down" ? { background: "rgba(17,14,57,0.055)", color: SOFT.ink } : undefined}
            >
              <ThumbsDown size={14} aria-hidden="true" fill={vote === "down" ? "currentColor" : "none"} />
            </button>
          </>
        ) : null}
        <button
          type="button"
          aria-label={
            copied
              ? t("ai_concierge.turn.copied", { defaultValue: "Copied" })
              : t("ai_concierge.turn.copy", { defaultValue: "Copy" })
          }
          onClick={copy}
          className={BTN}
        >
          {copied ? (
            <Check size={14} color="#1A7A44" aria-hidden="true" className="mj-pop-in" />
          ) : (
            <Copy size={14} aria-hidden="true" />
          )}
        </button>
        {shareSessionId ? (
          <button
            type="button"
            aria-label={
              shared
                ? t("shared_chat.share_copied", { defaultValue: "Link copied" })
                : t("shared_chat.share", { defaultValue: "Share this chat" })
            }
            onClick={() => void share()}
            className={BTN}
          >
            {shared ? (
              <Check size={14} color="#1A7A44" aria-hidden="true" className="mj-pop-in" />
            ) : (
              <Share2 size={14} aria-hidden="true" />
            )}
          </button>
        ) : null}
        {vote === "up" ? (
          <span className="mj-fade-in ms-1 text-[11.5px]" role="status">
            {t("ai_concierge.turn.thanks", { defaultValue: "Thanks." })}
          </span>
        ) : null}
      </div>

      {vote === "down" ? (
        reasonSent ? (
          <span className="mj-fade-in text-[11.5px]" style={{ color: SOFT.body }} role="status">
            {t("ai_concierge.turn.thanks", { defaultValue: "Thanks." })}
          </span>
        ) : (
          <form
            className="mj-fade-in flex max-w-[420px] items-center gap-1.5"
            onSubmit={(e) => {
              e.preventDefault();
              const why = reason.trim();
              if (!why) return;
              send("down", why.slice(0, 500));
              setReasonSent(true);
            }}
          >
            <label className="sr-only" htmlFor={`mj-vote-why-${turnId}`}>
              {t("ai_concierge.turn.what_missing", { defaultValue: "Thanks. What was missing?" })}
            </label>
            <input
              id={`mj-vote-why-${turnId}`}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("ai_concierge.turn.what_missing", { defaultValue: "Thanks. What was missing?" })}
              maxLength={500}
              className="min-h-[32px] min-w-0 flex-1 rounded-full bg-white px-3 text-[12.5px] outline-none placeholder:text-[#5a5d77]"
              style={{ color: SOFT.ink, boxShadow: `inset 0 0 0 1px ${SOFT.controlEdge}` }}
            />
            <button
              type="submit"
              disabled={!reason.trim()}
              className="mj-press min-h-[32px] rounded-full px-3 text-[12px] font-semibold transition-opacity disabled:opacity-40"
              style={{ background: SOFT.buttonFace, color: "#fff", boxShadow: SOFT.buttonInset }}
            >
              {t("ai_concierge.turn.send_reason", { defaultValue: "Send" })}
            </button>
          </form>
        )
      ) : null}
    </div>
  );
}
